"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.basicSafeSorter = basicSafeSorter;
exports.basicSorter = basicSorter;
exports.convertCaseOfModelKeys = convertCaseOfModelKeys;
exports.createCompositeSorter = createCompositeSorter;
exports.createUri = createUri;
exports.debugDisplay = debugDisplay;
exports.determineEnvironment = determineEnvironment;
exports.ensureDefined = ensureDefined;
exports.ensureOfType = ensureOfType;
exports.firstDefined = firstDefined;
exports.formatPostCode = formatPostCode;
exports.getFirstDefined = getFirstDefined;
exports.getFunctionName = getFunctionName;
exports.getOrdinal = getOrdinal;
exports.isTrue = isTrue;
exports.mapToQueryOrHashString = mapToQueryOrHashString;
exports.numberPrefixedStringSorter = numberPrefixedStringSorter;
exports.parseJson = parseJson;
exports.parseUri = parseUri;
exports.rangeWorker = rangeWorker;
exports.sortObjectKeys = sortObjectKeys;
exports.voidFunction = voidFunction;
var _lodash = require("@avantia/lodash");
var _queryString = _interopRequireDefault(require("query-string"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function voidFunction() {
  return;
}
function isTrue(v) {
  if (v === true || v === false) {
    return v;
  }
  if (v === undefined || v === null) {
    return false;
  }
  if (typeof v === 'number') {
    if (v === 1 || v === 0) {
      return v === 1;
    }
  }
  if (typeof v === 'string') {
    const lc = v.toLowerCase();
    if (lc === 'true' || lc === 'false' || lc === '' || lc === '1' || lc === '0') {
      return lc === 'true' || lc === '1';
    }
  }
  throw new Error(`There is no boolean representation of ${debugDisplay(v)}.`);
}
function getFirstDefined(...items) {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item === null || item === undefined) {
      continue;
    }
    return item;
  }
}
function getOrdinal(number, options) {
  const {
    lowerCase,
    shortForm
  } = options || {};
  let res = '';
  if (!shortForm) {
    switch (number) {
      case 1:
        res = 'First';
        break;
      case 2:
        res = 'Second';
        break;
      case 3:
        res = 'Third';
        break;
      case 4:
        res = 'Fourth';
        break;
      case 5:
        res = 'Fifth';
        break;
      case 6:
        res = 'Sixth';
        break;
      case 7:
        res = 'Seventh';
        break;
      case 8:
        res = 'Eighth';
        break;
      case 9:
        res = 'Ninth';
        break;
    }
  }
  if (!res) {
    const r = number === 11 || number === 12 || number === 13 ? number : number % 10;
    let suffix;
    switch (r) {
      case 1:
        suffix = 'st';
        break;
      case 2:
        suffix = 'nd';
        break;
      case 3:
        suffix = 'rd';
        break;
      default:
        suffix = 'th';
        break;
    }
    res = `${number}${suffix}`;
  }
  return lowerCase === true ? res.substr(0, 1).toLowerCase() + res.substr(1) : res;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function debugDisplay(value, quoteIfString) {
  const typeOfValue = typeof value;
  const ctorName = value && typeOfValue === 'object' ? getFunctionName(value.constructor) : undefined;
  if (typeOfValue === 'string') {
    return quoteIfString !== false ? `"${value}"` : value;
  } else if (_lodash.lodash.isDate(value)) {
    return value.toISOString();
  } else if (value === null || value === undefined) {
    return value === null ? 'null' : 'undefined';
  }
  if (ctorName && ctorName.endsWith('Error')) {
    const {
      name,
      message,
      stack
    } = value;
    if (message) {
      value = {
        message,
        name,
        stack,
        type: ctorName
      };
    }
  }
  return JSON.stringify(value, null, 2);
}
function parseJson({
  desc,
  json,
  verify
}) {
  let item;
  if (typeof json !== 'string') {
    throw new Error(`Failed to parse JSON for "${desc}": expected 'string' but got '${typeof json}'.`);
  }
  try {
    item = JSON.parse(json);
  } catch (err) {
    throw new Error(`Failed to parse JSON for "${desc}": ${err}\n${json}`);
  }
  if (verify) {
    const res = verify(item);
    if (res === false || res && typeof res === 'string') {
      throw new Error(`Failed parseJson 'verify' test for "${desc}": ${res || 'returned false'}\n${json}`);
    }
  }
  return item;
}
function rangeWorker(start, end, worker) {
  if (start <= end) {
    for (let i = start; i < end; i++) {
      if (!worker(i)) {
        break;
      }
    }
  } else {
    for (let i = start; i > end; i--) {
      if (!worker(i)) {
        break;
      }
    }
  }
}
function numberPrefixedStringSorter(a, b) {
  let ax = parseInt(a, 10);
  let bx = parseInt(b, 10);
  const nanA = isNaN(ax);
  const nanB = isNaN(bx);
  if (nanA || nanB) {
    if (nanA && nanB) {
      return basicSorter(a, b);
    }
    if (nanA) {
      ax = 0;
    } else {
      bx = 0;
    }
  }
  return basicSorter(ax, bx);
}

/**
 * Creates a composite sorter. This should be created once, rather each time in a function (and definitely never in a loop!)
 * When providing property keys rather than functions, be sure to check that the data type is supported.
 * @param param0 the values needed to create the sorter function.
 * @returns a sorter function.
 */
function createCompositeSorter({
  sorters,
  reverse
}) {
  const sorterFuncs = sorters.map(s => {
    if (typeof s === 'function') {
      return s;
    }
    return (a, b) => basicSorter(a[s], b[s]);
  });
  return (a, b) => {
    let res = 0;
    for (const sorter of sorterFuncs) {
      res = sorter(a, b);
      if (res !== 0) {
        break;
      }
    }
    if (reverse && res !== 0) {
      res = -res;
    }
    return res;
  };
}

/**
 * This is the same as the 'basicSorter' but handles null and undefined explicitly (and places these at the end).
 * @param a the value being compared
 * @param b the value being compared against.
 * @returns 1 if a > b, -1 if a < b or 0 if the values are the same.
 *          If both values are null/undefined/NaN, will return 0.
 *          If one value is null/undefined/NaN and the other is not, the other will always be seen as less (placing null/undefined/NaN last).
 */
function basicSafeSorter(a, b) {
  const aundef = a === null || a === undefined || typeof a === 'number' && isNaN(a);
  const bundef = b === null || b === undefined || typeof b === 'number' && isNaN(b);
  if (aundef || bundef) {
    if (bundef && aundef) {
      return 0;
    }
    return aundef ? 1 : -1;
  }
  return basicSorter(a, b);
}

/**
 * Basic sorter. It assumes that the types provided work natively against '>' and '<' operators.
 * This is not the case when DataT is a string but null or undefined may be provided, or when DataT is a number but NaN may be provided.
 * In these cases, use basicSafeSorter instead.
 * @param a the value being compared
 * @param b the value being compared against.
 * @returns 1 if a > b, -1 if a < b or 0 if the values are the same.
 */
function basicSorter(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Returns the unchanged 'value' parameter when the value provided is defined (i.e. not null and not undefined).
 * Throws an exception when the value is null or undefined.
 * @param value the value to check
 * @param name the name of the field to check (used to describe the failure)
 */
function ensureDefined(value, name) {
  if (value === undefined || value === null) {
    throw new Error(`The "${name}" cannot be null or undefined.`);
  }
  return value;
}
function ensureOfType(value, expectedType, name) {
  const valueType = typeof value;
  if (value === undefined || value === null) {
    throw new Error(`The "${name}" cannot be null or undefined.`);
  }
  if (valueType !== expectedType) {
    throw new Error(`The "${name}" must be of type ${expectedType} but was of type ${valueType} instead.`);
  }
  return value;
}

/**
 * Returns the first defined parameter. Throws an exception if none of the parameters is defined.
 * A 'defined' parameter is any parameter that is not null and not undefined.
 * @param a will be returned if it is defined.
 * @param b will be returned if it is defined and 'a' is not.
 * @param c will be returned if it is defined and both 'a' and 'b' are not.
 */
function firstDefined(a, b, c) {
  if (a !== undefined && a !== null) {
    return a;
  }
  if (b !== undefined && b !== null) {
    return b;
  }
  if (c !== undefined && c !== null) {
    return c;
  }
  throw new Error(`At leest one of the parameters must be defined.`);
}
function formatPostCode(postcode) {
  const rex = /([A-Z]{1,2}\d{1,2}[A-Z]?)(\d[A-Z]{2})/;
  const postcodeUpper = postcode.toUpperCase();
  let res = postcodeUpper.replace(/ /g, '');

  // https://en.wikipedia.org/wiki/Postcodes_in_the_United_Kingdom
  if (res.length >= 5 && res.length <= 7) {
    const data = rex.exec(res);
    if (data) {
      res = data[1] + ' ' + data[2];
    } else {
      res = postcodeUpper;
    }
  } else {
    res = postcodeUpper;
  }
  return res;
}

/**
 * Creates a URI with a query string.
 * If you need a hash string or need more control, use mapToQueryOrHashString instead.
 * @param uri the base URI.
 * @param query the query to append.
 */
function createUri({
  uri,
  query
}) {
  if (!query) {
    return uri;
  }
  return uri + mapToQueryOrHashString({
    map: query,
    sep: '&',
    prefix: '?',
    skipNull: true,
    sort: true
  });
}

/**
 * Creates a query string or hash string from a map.
 * @param map the map that will be used to create the result.
 * @param sep the separator character.
 * @param prefix if provided and the string is not empty, the 'prefix' value will be prefixed to the result.
 * @param skipNull skips a key if its value is null or undefined.
 * @param sort sorts the keys unless this value is false.
 */
function mapToQueryOrHashString({
  map,
  sep,
  prefix,
  skipNull,
  sort
}) {
  const keys = _lodash.lodash.keys(map);
  if (sort !== false) {
    keys.sort();
  }
  const res = keys.map(key => {
    const value = map[key];
    if (value === null || value === undefined) {
      return skipNull === true ? undefined : key;
    }
    return `${key}=${encodeURIComponent('' + map[key])}`;
  }).filter(v => v !== undefined).join(sep);
  return res && prefix ? prefix + res : res;
}
function parseUri(uri) {
  const opts = {
    parseBooleans: true,
    parseNumbers: true
  };
  const {
    url,
    query
  } = _queryString.default.parseUrl(uri, opts);
  return {
    uri: url,
    query: query
  };
}

// eslint-disable-next-line @typescript-eslint/ban-types
function getFunctionName(func) {
  if (typeof func !== 'function') {
    throw new Error(`The argument provided must be a function.`);
  }
  let name = func.toString().split('\n')[0].substring(9); // 9 = 'function '.length
  const ix = name.indexOf('(');
  name = name.substring(0, ix);
  return name;
}

/**
 * Converts keys of a model to the case specified by the 'keys' parameter.
 * Any keys in the model that are not specified in 'keys' will be removed.
 * If a key is specified in the 'keys' array but is not in the model it will not be added.
 * The original map will not changed.
 * @param {SMap<ValueT>} map the map that will have its keys normalised.
 * @param {string[]} keys the keys that should appear in the model.
 * @param {boolean} sort if not false, the model result will have its keys sorted.
 */
function convertCaseOfModelKeys(map, keys, sort) {
  const newKeysLower = keys.map(_lodash.lodash.toLower);
  const newMap = {};
  const modelKeys = _lodash.lodash.keys(map);
  if (sort !== false) {
    modelKeys.sort();
  }
  modelKeys.forEach(key => {
    const keyLower = key.toLowerCase();
    const ix = newKeysLower.indexOf(keyLower);
    if (ix >= 0) {
      const newKey = keys[ix];
      newMap[newKey] = map[key];
    }
  });
  return newMap;
}
/**
 * Sorts arrays and plain object keys (where 'plain' is as defined by lodash.isPlainObject).
 * @param record any object (but usually a plain object or an array)
 * @param recurse if true, will sort recursively, otherwise, only the top-level object will be sorted.
 * @returns a new object (if it's an array or plain object)
 */
function sortObjectKeys(record, options) {
  let sortArrays;
  let recurse;
  if (typeof options === 'boolean') {
    recurse = options;
  } else if (options) {
    ({
      sortArrays,
      recurse
    } = options);
  }
  if (recurse === undefined) {
    recurse = false;
  }
  if (sortArrays === undefined) {
    sortArrays = true;
  }
  return sortObjectKeysHelper(record, sortArrays, recurse);
}
function sortObjectKeysHelper(record, sortArrays, recurse) {
  if (_lodash.lodash.isArray(record)) {
    if (sortArrays) {
      const tmp = [...record].sort();
      tmp.forEach((el, ix) => {
        tmp[ix] = recurse ? sortObjectKeysHelper(el, sortArrays, recurse) : el;
      });
      return tmp;
    }
  } else if (_lodash.lodash.isPlainObject(record)) {
    const keys = _lodash.lodash.keys(record).sort();
    const tmp = {};
    keys.forEach(key => {
      tmp[key] = recurse ? sortObjectKeysHelper(record[key], sortArrays, recurse) : record[key];
    });
    return tmp;
  }
  return record;
}
function determineEnvironment() {
  let isClient = false;
  let isNode = false;
  try {
    if (window) {
      isClient = true;
    }
  } catch {
    try {
      if (process) {
        isNode = true;
      }
    } catch {
      // No timers for you!
    }
  }
  return {
    isClient,
    isNode
  };
}